import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import { DateCalendar, type DateCalendarProps } from '@mui/x-date-pickers';
import { useDisclosure } from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { ChevronLeftIcon, ChevronRightIcon } from '@tyro/icons';
import dayjs, { type Dayjs } from 'dayjs';
import LocalizedFormat from 'dayjs/plugin/localizedFormat';
import { useRef } from 'react';

dayjs.extend(LocalizedFormat);

interface DateSwitcherProps {
  onPreviousDateClick: () => void;
  onNextDateClick: () => void;
  date: Dayjs;
  onChangeDate: (date: Dayjs) => void;
  dateFormat?: string;
  calendarProps?: Partial<Omit<DateCalendarProps, 'value' | 'onChange'>>;
}

export function DateSwitcher({
  onPreviousDateClick,
  onNextDateClick,
  date,
  onChangeDate,
  dateFormat = 'LL',
  calendarProps,
}: DateSwitcherProps) {
  const dateButtonRef = useRef<HTMLButtonElement>(null);
  const { isOpen, id, onClose, getButtonProps } = useDisclosure();
  const { t } = useTranslation(['common', 'calendar']);

  const minDate = calendarProps?.minDate;
  const maxDate = calendarProps?.maxDate;
  const disabledPrevious =
    minDate && (date.isBefore(minDate, 'day') || date.isSame(minDate, 'day'));
  const disabledNext =
    maxDate && (date.isAfter(maxDate, 'day') || date.isSame(maxDate, 'day'));

  return (
    <Stack direction="row" alignItems="center" spacing={2}>
      <IconButton
        onClick={onPreviousDateClick}
        aria-label={t('common:actions.previous')}
        disabled={disabledPrevious}
      >
        <ChevronLeftIcon />
      </IconButton>

      <Button
        ref={dateButtonRef}
        variant="text"
        color="inherit"
        sx={{ fontWeight: 600 }}
        {...getButtonProps()}
      >
        {date.format(dateFormat)}
      </Button>
      <Popover
        open={isOpen}
        id={id}
        onClose={onClose}
        anchorEl={dateButtonRef.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <DateCalendar
          value={date}
          onChange={(newValue) => {
            if (newValue) {
              onChangeDate(newValue);
              onClose();
            }
          }}
          {...calendarProps}
        />
      </Popover>

      <IconButton
        onClick={onNextDateClick}
        aria-label={t('common:actions.next')}
        disabled={disabledNext}
      >
        <ChevronRightIcon />
      </IconButton>
    </Stack>
  );
}
